import type { SignupSchoolSelection } from '../components/signup/SignupSchoolFinder/SignupSchoolFinder';
import { getSignupProductByPlan, isDistrictAdminProductKey, isSignupProductKey, type DistrictAdminProductKey, type SignupProductKey } from '../config/SignupProductConfig';
import { getProgramBySignupPlan } from './ProgramService';
import type { CampusOption, StateOption } from './SchoolService';
import type { PreparedSignupContext } from './SignupService';
import { parseJson, stringifyJson } from './Utilities';

export type SignupMethod = 'email' | 'google' | 'canvas' | 'classlink' | 'clever' | 'schoology';
export type SignupOriginProvider = 'google' | 'canvas' | 'classlink' | 'clever' | 'schoology';

export interface StoredSignupFlow extends SignupSchoolSelection {
  version: 2;
  handoffId: string;
  subject: string;
  emailAddress: string;
  signupMethod?: SignupMethod;
  originProvider?: SignupOriginProvider;
  canvasState?: string;
  schoologyState?: string;
  subscriptionToken?: string;
  preparedTokenContext?: PreparedSignupContext;
  hasAcceptedTerms?: boolean;
  requestedProductKey?: SignupProductKey;
  districtAdminProductKey?: DistrictAdminProductKey;
  signupReturnPath?: string;
}

const signupFlowStorageKey = 'exploros.signup.standard-flow';
// Retain only navigation after one-use form details are consumed, so refresh keeps the right entry page.
const signupReturnStorageKey = 'exploros.signup.return-path';

export function createSignupHandoffId(): string {
  return Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function getSignupReturnPath(search: string): string {
  const query = new URLSearchParams(search);
  const plan = query.get('plan');
  const product = getSignupProductByPlan(plan);
  if (!product && !getProgramBySignupPlan(plan)) {
    return '/signup';
  }
  const returnQuery = new URLSearchParams({plan: plan!});
  if (product?.supportsDistrictAdmin && query.get('view') === 'district-admin') {
    returnQuery.set('view', 'district-admin');
  }
  return `/signup?${returnQuery.toString()}`;
}

function isSignupReturnPath(value: unknown): value is string {
  return typeof value === 'string' && (value === '/signup' ||
    value.startsWith('/signup?') && getSignupReturnPath(value.slice('/signup'.length)) === value);
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string';
}

function isStoredCampus(value: unknown): value is CampusOption {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const campus = value as Record<string, unknown>;
  return typeof campus.id === 'string'
    && typeof campus.name === 'string'
    && isOptionalString(campus.address)
    && isOptionalString(campus.city)
    && isOptionalString(campus.state)
    && isOptionalString(campus.zip);
}

function isStoredState(value: unknown): value is StateOption {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const state = value as Record<string, unknown>;
  return typeof state.code === 'string' && typeof state.label === 'string';
}

function isStoredPreparedTokenContext(value: unknown): value is PreparedSignupContext {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const context = value as Record<string, unknown>;
  return context.selectionMode === 'token'
    && typeof context.schoolName === 'string' && context.schoolName.trim() !== ''
    && (context.viewMode === undefined || context.viewMode === 'standard' || context.viewMode === 'product')
    && (context.productKey === undefined || isSignupProductKey(context.productKey))
    && (context.planName === undefined || typeof context.planName === 'string')
    && (context.state === undefined || isStoredState(context.state));
}

export function isStoredSignupFlow(value: unknown): value is StoredSignupFlow {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const flow = value as Record<string, unknown>;
  const isCanvasFlow = flow.signupMethod === 'canvas' || flow.originProvider === 'canvas';
  const hasValidCanvasPair = isCanvasFlow
    ? flow.signupMethod === 'canvas'
      && flow.originProvider === 'canvas'
      && typeof flow.canvasState === 'string'
      && flow.canvasState.trim() !== ''
      && flow.schoologyState === undefined
    : flow.canvasState === undefined;
  const isSchoologyFlow = flow.signupMethod === 'schoology' || flow.originProvider === 'schoology';
  const hasValidSchoologyPair = isSchoologyFlow
    ? flow.signupMethod === 'schoology'
      && flow.originProvider === 'schoology'
      && typeof flow.schoologyState === 'string'
      && flow.schoologyState.trim() !== ''
      && flow.canvasState === undefined
    : flow.schoologyState === undefined;
  const hasTokenRecovery = flow.subscriptionToken !== undefined || flow.preparedTokenContext !== undefined;
  const hasValidTokenRecovery = hasTokenRecovery
    ? typeof flow.subscriptionToken === 'string'
      && flow.subscriptionToken.trim() !== ''
      && isStoredPreparedTokenContext(flow.preparedTokenContext)
    : true;
  const hasValidDistrictAdmin = flow.districtAdminProductKey === undefined ||
    isSignupProductKey(flow.districtAdminProductKey)
      && isDistrictAdminProductKey(flow.districtAdminProductKey)
      && !hasTokenRecovery
      && flow.requestedProductKey === undefined
      && (flow.signupMethod === 'email' || flow.signupMethod === 'google');
  return flow.version === 2
    && typeof flow.handoffId === 'string' && /^[a-f0-9]{32}$/.test(flow.handoffId)
    && typeof flow.subject === 'string'
    && typeof flow.zipCode === 'string'
    && Array.isArray(flow.campuses)
    && flow.campuses.every(isStoredCampus)
    && typeof flow.campusId === 'string'
    && typeof flow.isMissingSchool === 'boolean'
    && Array.isArray(flow.stateOptions)
    && flow.stateOptions.every(isStoredState)
    && typeof flow.stateCode === 'string'
    && typeof flow.emailAddress === 'string'
    && (flow.signupMethod === undefined || flow.signupMethod === 'email' || flow.signupMethod === 'google' || flow.signupMethod === 'canvas' || flow.signupMethod === 'classlink' || flow.signupMethod === 'clever' || flow.signupMethod === 'schoology')
    && (flow.originProvider === undefined || flow.originProvider === 'google' || flow.originProvider === 'canvas' || flow.originProvider === 'classlink' || flow.originProvider === 'clever' || flow.originProvider === 'schoology')
    && hasValidCanvasPair
    && hasValidSchoologyPair
    && hasValidTokenRecovery
    && hasValidDistrictAdmin
    && (flow.requestedProductKey === undefined || isSignupProductKey(flow.requestedProductKey))
    && (flow.signupReturnPath === undefined || isSignupReturnPath(flow.signupReturnPath))
    && (flow.hasAcceptedTerms === undefined || typeof flow.hasAcceptedTerms === 'boolean');
}

function readStoredValue(): StoredSignupFlow | null {
  try {
    const storedValue = window.sessionStorage.getItem(signupFlowStorageKey);
    if (!storedValue) {
      return null;
    }
    const flow = parseJson<unknown>(storedValue);
    return isStoredSignupFlow(flow) ? flow : null;
  } catch {
    // Recovery is optional; unavailable browser storage uses the standalone result screen.
    return null;
  }
}

export function readStoredSignupFlow(handoffId: string): StoredSignupFlow | null {
  if (!/^[a-f0-9]{32}$/.test(handoffId)) {
    return null;
  }
  const flow = readStoredValue();
  return flow?.handoffId === handoffId ? flow : null;
}

export function readStoredProviderSignupFlow(provider: SignupOriginProvider): StoredSignupFlow | null {
  const flow = readStoredValue();
  return flow?.signupMethod === provider && flow.originProvider === provider ? flow : null;
}

export function writeStoredSignupFlow(flow: Omit<StoredSignupFlow, 'version'>): void {
  let hasSavedFormRecovery = false;
  try {
    window.sessionStorage.setItem(signupFlowStorageKey, stringifyJson({...flow, version: 2}));
    hasSavedFormRecovery = true;
    window.sessionStorage.setItem(signupReturnStorageKey, stringifyJson({
      handoffId: flow.handoffId,
      signupReturnPath: isSignupReturnPath(flow.signupReturnPath) ? flow.signupReturnPath : '/signup',
    }));
  } catch {
    // Some provider callbacks lack handoff IDs, so a failed replacement must not revive an older signup.
    if (!hasSavedFormRecovery) {
      clearStoredSignupFlow();
    }
    // Recovery is optional; blocked or full browser storage must not prevent signup.
  }
}

export function readSignupReturnPath(handoffId: string): string {
  if (!/^[a-f0-9]{32}$/.test(handoffId)) {
    return '/signup';
  }
  try {
    const saved = parseJson<unknown>(window.sessionStorage.getItem(signupReturnStorageKey) || 'null');
    if (saved && typeof saved === 'object' && 'handoffId' in saved && saved.handoffId === handoffId &&
        'signupReturnPath' in saved && isSignupReturnPath(saved.signupReturnPath)) {
      return saved.signupReturnPath;
    }
  } catch {
    // The generic signup page remains available if browser storage cannot be read.
  }
  return '/signup';
}

export function clearStoredSignupFlow(): void {
  try {
    window.sessionStorage.removeItem(signupFlowStorageKey);
  } catch {
    // Recovery cleanup must not interrupt result screens or successful sign-in.
  }
}
